import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { DollarSign, AlertCircle, CheckCircle2, CreditCard, Wallet, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { getFinancialStatusLabel } from '../constants/financialStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentModal = ({ open, onClose, operation, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  
  // Payment form state
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [isFinal, setIsFinal] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open && operation?.id) {
      fetchFinancialSummary();
    }
  }, [open, operation?.id]);

  const fetchFinancialSummary = async () => {
    setLoadingSummary(true);
    try {
      const response = await axios.get(
        `${API}/financial/${operation.id}/summary`,
        {
          headers: { 'Authorization': 'Bearer cookie-auth' }
        }
      );
      setFinancialSummary(response.data);
      
      // Pre-fill remaining amount
      if (response.data.amountRemaining > 0) {
        setAmount(response.data.amountRemaining.toString());
      }
    } catch (error) {
      console.error('Error fetching financial summary:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger le résumé financier",
        variant: "destructive"
      });
    } finally {
      setLoadingSummary(false);
    }
  };

  const validateForm = () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: "Champ manquant",
        description: "Le montant du paiement est obligatoire",
        variant: "destructive"
      });
      return false;
    }
    
    if (!paymentMethod) {
      toast({
        title: "Champ manquant",
        description: "La méthode de paiement est obligatoire",
        variant: "destructive"
      });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/financial/${operation.id}/record-payment`,
        {
          amount: parseFloat(amount),
          payment_method: paymentMethod,
          payment_reference: paymentReference || null,
          is_final: isFinal,
          note: note || null
        },
        {
          headers: {
            'Authorization': 'Bearer cookie-auth',
            'Content-Type': 'application/json'
          }
        }
      );
      
      const isFullPayment = response.data.new_status === 'paid';
      
      toast({
        title: isFullPayment ? "✅ Paiement complet enregistré" : "✅ Paiement partiel enregistré",
        description: isFullPayment 
          ? "Le paiement a été enregistré. L'opération est marquée comme payée."
          : `Paiement de ${parseFloat(amount).toLocaleString('fr-FR')} XOF enregistré. Reste à payer: ${response.data.remaining?.toLocaleString('fr-FR')} XOF`
      });
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
      
    } catch (error) {
      console.error('Error recording payment:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Une erreur est survenue lors de l'enregistrement du paiement",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return value?.toLocaleString('fr-FR') || '0';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-emerald-600" />
            Enregistrer un paiement
          </DialogTitle>
          <DialogDescription>
            Enregistrez un paiement partiel ou complet pour cette opération
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Financial Summary */}
          {loadingSummary ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : financialSummary && (
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4">
              <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                Résumé financier
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Statut financier:</span>
                  <span className="font-medium text-emerald-700">
                    {getFinancialStatusLabel(financialSummary.financialStatus)}
                  </span>
                </div>
                {financialSummary.invoiceNumber && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">N° Facture:</span>
                    <span className="font-medium">{financialSummary.invoiceNumber}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Montant facturé:</span>
                  <span className="font-medium">{formatCurrency(financialSummary.invoiceAmount)} XOF</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Déjà payé:</span>
                  <span className="font-medium text-blue-600">{formatCurrency(financialSummary.amountPaid)} XOF</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-emerald-200">
                  <span className="font-semibold text-emerald-800">Reste à payer:</span>
                  <span className="font-bold text-emerald-800">{formatCurrency(financialSummary.amountRemaining)} XOF</span>
                </div>
              </div>
            </div>
          )}

          {/* Warning: payment already fully received */}
          {financialSummary && financialSummary.financialStatus === 'paid' && (
            <Alert className="border-amber-300 bg-amber-50" data-testid="payment-already-complete-warning">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Attention :</strong> Le paiement de cette opération est déjà enregistré comme <strong>complet</strong>.
                Tout nouveau paiement sera ajouté en supplément (correction, avoir, etc.)
              </AlertDescription>
            </Alert>
          )}

          {financialSummary && financialSummary.amountRemaining <= 0 && financialSummary.financialStatus !== 'paid' && (
            <Alert className="border-amber-300 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                Le solde restant est de <strong>0 XOF</strong>. La facture est théoriquement soldée.
              </AlertDescription>
            </Alert>
          )}
          
          {/* Payment Form */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="amount">Montant du paiement (XOF) *</Label>
              <Input
                id="amount"
                type="number"
                min="0"
                step="100"
                placeholder="ex: 500000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
              {financialSummary?.amountRemaining > 0 && (
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAmount(financialSummary.amountRemaining.toString())}
                  >
                    Solde total ({formatCurrency(financialSummary.amountRemaining)} XOF)
                  </Button>
                  {financialSummary.amountRemaining > 100000 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setAmount((financialSummary.amountRemaining / 2).toString())}
                    >
                      50% ({formatCurrency(financialSummary.amountRemaining / 2)} XOF)
                    </Button>
                  )}
                </div>
              )}
            </div>
            
            <div>
              <Label htmlFor="paymentMethod">Méthode de paiement *</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger id="paymentMethod">
                  <SelectValue placeholder="Sélectionner une méthode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="virement">Virement bancaire</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  <SelectItem value="orange_money">Orange Money</SelectItem>
                  <SelectItem value="mtn_money">MTN Mobile Money</SelectItem>
                  <SelectItem value="wave">Wave</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="especes">Espèces</SelectItem>
                  <SelectItem value="carte">Carte bancaire</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="paymentReference">Référence du paiement (optionnel)</Label>
              <Input
                id="paymentReference"
                type="text"
                placeholder="ex: VIR-2025-001234"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Numéro de transaction, référence bancaire, etc.
              </p>
            </div>
            
            <div>
              <Label htmlFor="note">Note (optionnel)</Label>
              <Input
                id="note"
                type="text"
                placeholder="Note sur le paiement..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <input
                type="checkbox"
                id="isFinal"
                checked={isFinal}
                onChange={(e) => setIsFinal(e.target.checked)}
                className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
              />
              <Label htmlFor="isFinal" className="cursor-pointer text-sm">
                <span className="font-medium text-amber-800">Marquer comme paiement final</span>
                <p className="text-amber-700 text-xs mt-0.5">
                  Même si le montant est inférieur au solde, l&apos;opération sera marquée comme payée
                </p>
              </Label>
            </div>
          </div>
          
          {/* Warning for partial payment */}
          {amount && financialSummary?.amountRemaining && parseFloat(amount) < financialSummary.amountRemaining && !isFinal && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Ce paiement est partiel. Il restera <strong>{formatCurrency(financialSummary.amountRemaining - parseFloat(amount))} XOF</strong> à payer.
              </AlertDescription>
            </Alert>
          )}
          
          {/* Success indicator for full payment */}
          {amount && financialSummary?.amountRemaining && parseFloat(amount) >= financialSummary.amountRemaining && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Ce paiement soldera l&apos;intégralité de la facture.
              </AlertDescription>
            </Alert>
          )}
        </div>
        
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Enregistrer le paiement
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
